<template>
  <div>
    <b-alert variant="danger" :show="customerData === undefined">
      <h4 class="alert-heading">Error fetching customer data</h4>
      <div class="alert-body">
        No Progress found with this customer id. Check
        <b-link class="alert-link" :to="{ name: 'customer-list' }">
          customer List
        </b-link>
        for other customer.
      </div>
    </b-alert>
    <div v-if="customerData">
      <b-card no-body class="p-1 px-2">
        <!-- Customer Info: Top col -->
        <div
          class="d-flex flex-wrap align-items-center justify-content-between"
        >
          <h4 class="mb-0 ml-0 font-weight-bolder text-nowrap">客戶資料</h4>
          <div class="d-flex flex-wrap justify-content-end">
            <b-button
              v-if="ability.can('update', 'setting_customers')"
              class="px-3 mx-1 backgroundColor1 no-border"
              :to="{
                name: 'customer-edit',
                params: { id: customerData.id },
              }"
              ><feather-icon
                class="mr-50"
                size="14"
                icon="Edit2Icon"
              />修改</b-button
            >
            <b-button
              class="px-3 mx-1"
              type="button"
              variant="outline-secondary"
              @click="hide"
            >
              返回
            </b-button>
            <b-button
              class="px-3 mx-1 no-border backgroundColor3 colorBlack"
              variant="outline-danger"
              v-if="ability.can('delete', 'setting_customers')"
              @click="deleteCustomer(customerData.id)"
              ><feather-icon
                class="mr-50"
                size="14"
                icon="Trash2Icon"
              />刪除</b-button
            >
          </div>
        </div>
        <hr />
        <b-row class="info-wrap px-1">
          <b-col cols="12" sm="4">
            <div class="mb-1">
              <label class="mr-50">編號：</label>{{ customerData.user_number }}
            </div>
            <h4 class="mb-2 font-weight-bolder">
              {{ customerData.name_cn }}
            </h4>
          </b-col>
          <b-col cols="12" sm="4">
            <div class="info-item">
              <b class="info-item-label">聯絡電話：</b>
              <span>{{ customerData.phone }}</span>
            </div>
            <div class="info-item">
              <b class="info-item-label">電郵地址：</b>
              <span>{{ customerData.email }}</span>
            </div>
            <div class="info-item">
              <b class="info-item-label">地址：</b>
              <pre>{{ customerData.customer_addresses.join("\n") }}</pre>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <b-row>
        <b-col
          md="12"
        >
          <!-- 發票記錄 -->
          <b-card class="no-min pdf-table-wrapper-box">
            <div style="background-color: #e1f2fe" class="colored-tab-title">
              <h4
                class="d-flex aling-items-center p-1 left-bold-border before-color-0 font-weight-bolder"
              >
                報價單記錄
              </h4>
            </div>
            <div class="mt-2">
              <b-table
                ref="refQuotationListTable"
                class="customer-table-quotation position-relative"
                :items="fetchQuotations"
                responsive
                :fields="quotationColumns"
                primary-key="id"
                :sort-by.sync="sortByQuotation"
                show-empty
                empty-text="未找到匹配的記錄"
                :sort-desc.sync="isSortDirDescQuotation"
              >
                <!-- Column: quotation_type_id -->
                <template #cell(quotation_type_id)="data">
                  {{ getInvoiceType(data.item.quotation_type_id).name }}
                </template>

                <template #cell(id)="data">
                    <b-link :to="{ name: 'quotation-edit', params: { id: data.item.id } }" class="text-primary">
                      {{ data.item.quotation_number }}
                    </b-link>
                </template>

                <!-- Column: salary_items -->
                <template #cell(show_details)="row">
                  <b-button
                    v-if="row.item.cost_items"
                    size="sm"
                    @click="row.toggleDetails()"
                    class="mr-2"
                  >
                    {{ row.detailsShowing ? "隱藏" : "顯示" }}
                  </b-button>
                </template>

                <template #row-details="row">
                  <div
                    class="info-container"
                    v-if="row.item.cost_items && row.item.cost_items.length"
                  >
                    <b-row
                      class="p-2 backgroundColor3 mb-1"
                      v-for="(cost, index) in row.item.cost_items"
                      :key="`cost${index}`"
                    >
                      <b-col cols="12" md="4">
                        <div class="row mb-50">
                          <b class="label">商品分類：</b>
                          <span>{{
                            getProductCategory(cost.product_category_id).name
                          }}</span>
                        </div>

                        <div class="row mb-50">
                          <b class="label">商品：</b>
                          <span>{{ getProduct(cost.product_id).product_name }}</span>
                        </div>

                        <div class="row mb-50">
                          <b class="label">金額：</b>
                          <span
                            >{{
                              parseFloat(Number(cost.quantity).toString()) +
                              getProductUnit(cost.product_id)
                            }}
                            * ${{ cost.unit_price }} = ${{ cost.amount }}</span
                          >
                        </div>
                      </b-col>
                      <b-col cols="12" md="4">
                        <div class="row mb-50">
                          <b class="label">增加空行數量：</b>
                          <pre>{{ cost.empty_rows }}</pre>
                        </div>

                        <div class="row mb-50">
                          <b class="label">備註/尺寸：</b>
                          <span>{{ cost.item_description }}</span>
                        </div>
                      </b-col>
                    </b-row>
                  </div>
                </template>

                <!-- Column: stamp -->
                <template #cell(stamp)="data">
                  <feather-icon
                    v-if="data.item.stamp_id"
                    icon="CheckIcon"
                    size="18"
                    class="mr-50 text-success"
                  />
                </template>

                <template #cell(created_at)="data">
                  {{ getDateTime(data.item.created_at) }}
                </template>

                <template #cell(actions)="data">
                  <b-link
                    v-if="
                      ability.can('quotations_print', 'customer_detail') || true
                    "
                    :to="{
                      name: 'quotation-preview',
                      params: { id: data.item.id },
                    }"
                  >
                    <feather-icon icon="FileTextIcon" />
                    <span class="align-middle ml-50">列印報價單</span>
                  </b-link>
               
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>
        <b-col
          md="12"
        >
          <!-- 發票記錄 -->
          <b-card class="no-min pdf-table-wrapper-box">
            <div style="background-color: #e1f2fe" class="colored-tab-title">
              <h4
                class="d-flex aling-items-center p-1 left-bold-border before-color-0 font-weight-bolder"
              >
                發票記錄
              </h4>
            </div>
            <div class="mt-2">
              <b-table
                ref="refInvoiceListTable"
                class="customer-table-invoice position-relative"
                :items="fetchInvoices"
                responsive
                :fields="invoiceColumns"
                primary-key="id"
                :sort-by.sync="sortBy"
                show-empty
                empty-text="未找到匹配的記錄"
                :sort-desc.sync="isSortDirDesc"
              >
                <!-- Column: invoice_type -->
                <template #cell(invoice_type)="data">
                  {{
                    getInvoiceType(data.item.invoice_type_id) &&
                    getInvoiceType(data.item.invoice_type_id).name
                  }}
                </template>

                <template #cell(quotation_id)="data">
                  {{
                    getQuotation(data.item.quotation_id) &&
                    getQuotation(data.item.quotation_id).quotation_number
                  }}
                </template>

                <template #cell(invoice_number)="data">
                  <b-link :to="{ name: 'invoice-edit', params: { id: data.item.id } }" class="text-primary">
                    {{ data.item.invoice_number }}
                    </b-link>
                </template>

                <template #cell(total_unpaid)="data">
                  {{ data.item.total_amount - data.item.total_paid }}
                </template>

                <!-- Column: cost_items -->
                <template #cell(show_details)="row">
                  <b-button v-if="row.item.cost_items" size="sm" @click="toggleItemDetails(row)" class="mr-2">
                    {{ getItemDetailsShowing(row.item.id) ? "隱藏" : "顯示" }}
                  </b-button>
                </template>

                <!-- Column: payment_steps -->
                <template #cell(show_details2)="row">
                  <b-button v-if="
                    (row.item.payment_steps && row.item.payment_steps.length > 0) ||
                    (row.item.paymentSteps && row.item.paymentSteps.length > 0)
                  " size="sm" @click="togglePaymentStepsDetails(row)" class="mr-2" variant="primary">
                    {{ getPaymentStepsShowing(row.item.id) ? "隱藏" : "顯示" }}
                  </b-button>
                  <span v-else class="text-muted">還沒設置階段收費</span>
                </template>

                <template #row-details="row">
                  <!-- 項目摘要詳情 -->
                  <div v-if="getItemDetailsShowing(row.item.id)">
                    <div class="info-container" v-if="row.item.salary_items && row.item.salary_items.length">
                      <b-row class="p-2 backgroundColor5 mb-1" v-for="(worker, index) in row.item.salary_items"
                        :key="`cost${index}`">
                        <b-col cols="12" md="3">
                          <div class="row mb-50">
                            <b class="label">師傅：</b>
                            <span>{{ getWorkerName(worker.worker_id) }}</span>
                          </div>
                          <div class="row mb-50">
                            <b class="label">職位：</b>
                            <span>{{ worker.position }}</span>
                          </div>
                          <div class="row mb-50">
                            <b class="label">工資：</b>
                            <span>{{ worker.working_days }}天 * ${{
                              Number(worker.day_salary).toFixed(2)
                              }}每日 = ${{
                                Number(worker.total_salary).toFixed(2)
                              }}</span>
                          </div>
                        </b-col>
                        <b-col cols="12" md="3">
                          <div class="row mb-50">
                            <b class="label">開工日期：</b>
                            <pre>{{ worker.working_date }}</pre>
                          </div>
                        </b-col>
                      </b-row>
                    </div>
                    <div class="info-container" v-if="row.item.cost_items && row.item.cost_items.length">
                      <b-row class="p-2 backgroundColor3 mb-1" v-for="(cost, index) in row.item.cost_items"
                        :key="`cost${index}`">
                        <b-col cols="12" md="3">
                          <div class="row mb-50">
                            <b class="label">商品分類：</b>
                            <span>{{
                              getProductCategory(cost.product_category_id).name
                            }}</span>
                          </div>
                          <div class="row mb-50">
                            <b class="label">商品：</b>
                            <span>{{
                              getProduct(cost.product_id).product_name
                            }}</span>
                          </div>
                          <div class="row mb-50">
                            <b class="label">金額：</b>
                            <span>{{ parseFloat(Number(cost.quantity).toString()) }}
                              {{ getProduct(cost.product_id).unit }} * ${{
                                cost.unit_price
                              }}
                              = ${{ cost.amount }}</span>
                          </div>
                        </b-col>
                        <b-col cols="12" md="3">
                          <div class="row mb-50">
                            <b class="label">備註/尺寸：</b>
                            <span>{{ cost.item_description }}</span>
                          </div>
                        </b-col>
                      </b-row>
                    </div>
                  </div>

                  <!-- 階段收費詳情 -->
                  <div v-if="getPaymentStepsShowing(row.item.id)" class="payment-steps-details">
                    <div class="info-container">
                      <h6 class="mb-3 text-primary">
                        <feather-icon icon="CreditCardIcon" size="16" class="mr-1" />
                        階段收費詳情
                        <span v-if="row.item.payment_steps_title" class="text-muted">
                          - {{ row.item.payment_steps_title }}
                        </span>
                      </h6>
                      <div v-if="
                        getPaymentStepsData(row.item) &&
                        getPaymentStepsData(row.item).length > 0
                      ">
                        <b-row class="p-1 mb-1 backgroundColor2 border-left-primary"
                          v-for="(step, index) in getPaymentStepsData(row.item)" :key="`payment_step_${index}`">
                          <b-col cols="12" class="d-flex">
                            <div class="d-flex mr-2">
                              <b class="label">階段：</b>
                              <span>{{ step.step_phase }}</span>
                            </div>

                            <div class="d-flex mr-2">
                              <b class="label">佔比：</b>
                              <span class="text-info font-weight-bold">{{ step.step_percentage }}%</span>
                            </div>

                            <div class="d-flex mr-2">
                              <b class="label">金額：</b>
                              <span class="text-success font-weight-bold">${{ Number(step.step_amount).toFixed(2)
                                }}</span>
                            </div>

                            <div class="d-flex" v-if="step.step_name">
                              <b class="label">備註：</b>
                              <span>{{ step.step_name }}</span>
                            </div>
                          </b-col>
                        </b-row>
                        <b-row class="p-1 mb-1 backgroundColor2 border-left-primary">
                          <small class="text-info">
                            <strong>總計：</strong>
                            {{
                              getPaymentStepsData(row.item)
                                .reduce(
                                  (sum, step) => sum + Number(step.step_percentage),
                                  0
                                )
                                .toFixed(2)
                            }}% = ${{
                              getPaymentStepsData(row.item)
                                .reduce(
                                  (sum, step) => sum + Number(step.step_amount),
                                  0
                                )
                                .toFixed(2)
                            }}
                          </small>
                        </b-row>
                      </div>
                      <div v-else class="text-center text-muted py-3">
                        <feather-icon icon="InfoIcon" size="16" class="mr-1" />
                        此發票沒有設置階段收費信息
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Column: stamp -->
                <template #cell(stamp)="data">
                  <feather-icon
                    v-if="data.item.stamp"
                    icon="CheckIcon"
                    size="18"
                    class="mr-50 text-success"
                  />
                </template>

                <template #cell(created_at)="data">
                  {{ getDateTime(data.item.created_at) }}
                </template>

                <template #cell(actions)="data">
                  <b-link
                    v-if="
                      ability.can('invoices_print', 'customer_detail') || true
                    "
                    :to="{
                      name: 'invoices-preview',
                      params: { id: data.item.id },
                    }"
                  >
                    <feather-icon icon="FileTextIcon" />
                    <span class="align-middle ml-50">列印發票</span>
                  </b-link>
                  <!-- <b-link
                v-if="ability.can('update', 'invoices') "
                :to="{
                  name: 'receipt-create',
                  query: { invoice_id: data.item.id },
                }"
                class="ml-1"
              >
                <feather-icon icon="FileTextIcon" />
                <span class="align-middle ml-50">生成收據</span>
              </b-link> -->
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>

        <!-- 收據 -->
        <b-col
          md="12"
        >
          <b-card style="height: calc(100% - 2rem)">
            <div class="d-flex align-items-center justify-content-between">
              <h4
                class="mb-0 ml-1 d-flex aling-items-center left-bold-border before-color-0 font-weight-bolder"
              >
                收據
              </h4>

              <!-- <b-button
                variant="primary"
                class="d-flex py-50 px-3 align-items-center"
                @click="handleModalSalaryRecord(false)"
              >
                <feather-icon icon="PlusIcon" size="20" />
                新增</b-button
              > -->
            </div>
            <div class="mt-2">
              <b-table
                ref="refReceiptListTable"
                class="position-relative"
                :items="fetchReceipts"
                responsive
                :fields="receiptTablemColumns"
                primary-key="id"
                :sort-by.sync="sortReceiptBy"
                show-empty
                empty-text="未找到匹配的記錄"
                :sort-desc.sync="isSortDirDescReceipt"
              >
                <template #cell(payment_method)="data">
                  {{ getPaymentMethod(data.item.payment_method_id).name }}
                </template>

                <template #cell(id)="data">
                  <b-link :to="{ name: 'receipt-edit', params: { id: data.item.id } }" class="text-primary">
                    {{ data.item.receipt_number }}
                  </b-link>
                </template>

                <!-- Column: show_details receipt_items -->
                <template #cell(show_details)="row">
                  <b-button
                    v-if="row.item.receipt_items"
                    size="sm"
                    @click="row.toggleDetails"
                    class="mr-2"
                  >
                    {{ row.detailsShowing ? "隱藏" : "顯示" }}
                  </b-button>
                </template>
                <template #row-details="row">
                  <b-row class="p-2 backgroundColor5 mb-1" v-for="(receipt, index) in row.item.receipt_items"
                    :key="`receipt${index}`">
                    <b-col cols="12" md="4" class="mb-50">
                      <span class="">發票編號：</span>
                      <span class="">
                        <a v-if="receipt.invoice && receipt.invoice.invoice_number && receipt.invoice.id"
                          :href="$router.resolve({ name: 'invoice-edit', params: { id: receipt.invoice.id } }).href"
                          target="_blank" class="text-primary">
                          {{ receipt.invoice.invoice_number }}
                        </a>
                        <span v-else>
                          {{ receipt.invoice && receipt.invoice.invoice_number }}
                        </span>
                      </span>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-50">
                      <span class="">付款日期：</span>
                      <span class="">{{ receipt.payment_date }}</span>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-50">
                      <span class="">付款方式：</span>
                      <span class="">{{ receipt.payment_method && receipt.payment_method.name }}</span>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-50">
                      <span class="">付款金額：</span>
                      <div v-if="receipt.is_reprint" class="reprint-amount text-warning">
                        <div class="font-weight-bold">${{ Number(receipt.amount).toFixed(2) }}</div>
                        <small class="text-muted">
                          (原額: ${{ getOriginalReceiptAmount(receipt) }})
                        </small>
                      </div>
                      <span v-else class="">${{ Number(receipt.amount).toFixed(2) }}</span>
                    </b-col>
                    <b-col cols="12" md="4" class="mb-50">
                      <!-- <span class="">款項摘要：</span> -->
                      <span class="" :class="{ 'text-warning': receipt.is_reprint }">
                        {{ receipt.payment_step ?
                          `${receipt.payment_step.step_phase} ${receipt.payment_step.step_name ? `：
                        ${receipt.payment_step.step_name}` : ''} - 金額：$${receipt.payment_step.step_amount}`
                          : '' }}
                        <span v-if="receipt.is_reprint" class="badge badge-warning ml-1">(Re-Print)</span>
                      </span>
                    </b-col>
                  </b-row>
                </template>

                <template #cell(export)="data">
                  <b-link
                    v-if="
                      ability.can('receipts_print', 'customer_detail') || true
                    "
                    :to="{
                      name: 'receipt-preview',
                      params: { id: data.item.id },
                    }"
                  >
                    <feather-icon icon="FileTextIcon" />
                    <span class="align-middle ml-50">列印收據</span>
                  </b-link>
                </template>

                <template #cell(created_at)="data">
                  {{ getDateTime(data.item.created_at) }}
                </template>
              </b-table>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BTable,
  BRow,
  BCol,
  BAlert,
  BLink,
  BButton,
  BDropdown,
  BDropdownItem,
  BCollapse,
  VBToggle,
} from "bootstrap-vue";
import { ref, onUnmounted, onMounted } from "@vue/composition-api";
import vSelect from "vue-select";
import store from "@/store";
import { formatCurrency } from "@core/utils/filter";
import router from "@/router";
import customerStoreModule from "../customerStoreModule";
import ability from "@/libs/acl/ability";

export default {
  components: {
    BCard,
    BCardBody,
    BTable,
    BRow,
    BCol,
    BAlert,
    BLink,
    BButton,
    BDropdown,
    BDropdownItem,
    BCollapse,
    vSelect,
  },
  data() {
    return {
      ability,
      documentData: null,
      pdfUrl: null,
      loadingPDF: false,
      title: null,
      previewImage: null,
      // 獨立管理兩種詳情顯示狀態
      detailsShowing: {}, // 項目摘要詳情顯示狀態 { invoiceId: boolean }
      paymentStepsShowing: {}, // 階段收費詳情顯示狀態 { invoiceId: boolean }
    };
  },
  directives: {
    "b-toggle": VBToggle,
  },
  methods: {
    hide() {
      this.$router.replace("/users/list");
    },
    // handleModelDocument(item) {
    //   if (item) {
    //     this.documentData = item;
    //   } else {
    //     this.documentData = { id: 0 };
    //   }
    //   setTimeout(() => {
    //     this.$bvModal.show("modal-document");
    //   }, 100);
    // },
    deleteCustomer(id) {
      this.$swal({
        title: "你確定嗎?",
        text: "您的決定是最終決定，您將無法取回這記錄。",
        showCancelButton: true,
        confirmButtonText: "刪除",
        cancelButtonText: "取消",
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-outline-danger ml-1",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.value) {
          this.$store
            .dispatch("customer/deleteCustomer", { id })
            .then((response) => {
              this.$swal({
                text: response.data.message,
                icon: "success",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
              this.refetchData();
            })
            .catch((error) => {
              this.$swal({
                text: error.response.data.message,
                icon: "error",
                customClass: {
                  confirmButton: "btn btn-primary",
                },
                showClass: {
                  popup: "animate__animated animate__bounceIn",
                },
                buttonsStyling: false,
              });
            });
        }
      });
    },
    makeToast(variant = null, title = null, content = null) {
      this.$bvToast.toast(content, {
        title: title,
        variant,
        solid: true,
      });
    },
    // 項目摘要詳情管理方法
    toggleItemDetails(row) {
      const invoiceId = row.item.id;
      const wasPaymentStepsShowing = this.paymentStepsShowing[invoiceId];
      const wasItemDetailsShowing = this.detailsShowing[invoiceId];

      // 關閉階段收費詳情（如果開啟的話）
      this.$set(this.paymentStepsShowing, invoiceId, false);
      // 切換項目摘要詳情
      this.$set(this.detailsShowing, invoiceId, !wasItemDetailsShowing);

      // 只有在需要時才調用 row.toggleDetails()
      if (!wasPaymentStepsShowing && !wasItemDetailsShowing) {
        // 如果之前兩個都沒有顯示，需要打開 row details
        row.toggleDetails();
      } else if (
        !this.detailsShowing[invoiceId] &&
        !this.paymentStepsShowing[invoiceId]
      ) {
        // 如果現在兩個都沒有顯示，需要關閉 row details
        row.toggleDetails();
      }
    },

    getItemDetailsShowing(invoiceId) {
      return !!this.detailsShowing[invoiceId];
    },

    // 階段收費詳情管理方法
    togglePaymentStepsDetails(row) {
      const invoiceId = row.item.id;
      const wasItemDetailsShowing = this.detailsShowing[invoiceId];
      const wasPaymentStepsShowing = this.paymentStepsShowing[invoiceId];

      // 關閉項目摘要詳情（如果開啟的話）
      this.$set(this.detailsShowing, invoiceId, false);
      // 切換階段收費詳情
      this.$set(this.paymentStepsShowing, invoiceId, !wasPaymentStepsShowing);

      // 只有在需要時才調用 row.toggleDetails()
      if (!wasItemDetailsShowing && !wasPaymentStepsShowing) {
        // 如果之前兩個都沒有顯示，需要打開 row details
        row.toggleDetails();
      } else if (
        !this.detailsShowing[invoiceId] &&
        !this.paymentStepsShowing[invoiceId]
      ) {
        // 如果現在兩個都沒有顯示，需要關閉 row details
        row.toggleDetails();
      }
    },

    getPaymentStepsShowing(invoiceId) {
      return !!this.paymentStepsShowing[invoiceId];
    },

    // 獲取階段收費數據（兼容不同的數據格式）
    getPaymentStepsData(item) {
      // 優先使用 paymentSteps（後端關聯名稱），其次使用 payment_steps
      return item.paymentSteps || item.payment_steps || [];
    },

    // 獲取原始收據金額（用於重印收據）
    getOriginalReceiptAmount(receipt) {
      // 這裡需要根據實際邏輯來實現
      return receipt.original_amount || receipt.amount;
    },
  },
  setup() {
    const customerData = ref(null);
    const customer_APP_STORE_MODULE_NAME = "customer";

    if (!store.hasModule(customer_APP_STORE_MODULE_NAME))
      store.registerModule(customer_APP_STORE_MODULE_NAME, customerStoreModule);

    // UnRegister on leave
    onUnmounted(() => {
      if (store.hasModule(customer_APP_STORE_MODULE_NAME))
        store.unregisterModule(customer_APP_STORE_MODULE_NAME);
    });

    const refQuotationListTable = ref(null);
    const quotationColumns = [
      { key: "id", label: "編號", class: "sticky-column", sortable: true },
      {
        key: "quotation_date",
        class: "sticky-column",
        label: "發單日期",
        sortable: true,
      },
      { key: "actions", label: "列印報價單", class: "sticky-column" },
      { key: "show_details", label: "項目摘要" },
      { key: "stamp", label: "公司印章" },
      { key: "customer.name_cn", label: "單據抬頭" },
      { key: "quotation_type_id", label: "類別", sortable: true },
      { key: "project_name", label: "項目名稱", sortable: true },
      { key: "address", label: "施工地址", sortable: true },
      { key: "total_amount", label: "總價", formatter: formatCurrency },
      /* { key: "name_en", label: "單據抬頭" }, */
      { key: "mobile", label: "聯絡電話" },
      { key: "email", label: "電郵地址" },
      { key: "created_at", label: "創建日期", sortable: true },
    ];
    const sortByQuotation = ref("id");
    const isSortDirDescQuotation = ref(true);

    const refInvoiceListTable = ref(null);
    const invoiceColumns = [
      { key: "invoice_number", label: "編號", class: 'sticky-column', sortable: true },
      { key: "invoice_date", class: 'sticky-column', label: "發單日期", sortable: true },
      { key: "actions", label: "操作", class: 'sticky-column' },
      { key: "show_details2", label: "階段收費" },
      { key: "show_details", label: "項目摘要" },
      { key: "stamp", label: "公司印章" },
      { key: "customer.name_cn", label: "單據抬頭" },
      { key: "invoice_type", label: "類別" },
      { key: "project_name", label: "項目名稱", sortable: true },
      { key: "address", label: "施工地址", sortable: true },
      { key: "amount", label: "總價", sortable: true, formatter: formatCurrency },
      // { key: "deposit_amount", label: "第一次付款金額", sortable: true, formatter: formatCurrency },
      // { key: 'total_deposit_paid', label: "已付第一次付款", formatter: formatCurrency },
      { key: 'total_paid', label: "已付", formatter: formatCurrency },
      { key: 'total_unpaid', label: "未付", formatter: formatCurrency },
      { key: 'total_refund', label: "總退款額", formatter: formatCurrency },
      { key: 'total_bad_debt_amount', label: "總壞帳額", formatter: formatCurrency },
      // { key: "deposit_due_date", label: "第一次付款截止日期", sortable: true },
      // { key: "deposit_paid_date", label: "第一次付款支付期日", sortable: true },
      /*
      { key: "name_en", label: "系統帳號名稱" }, */
      { key: "mobile", label: "聯絡電話" },
      { key: "email", label: "電郵地址" },
      { key: "quotation_id", label: "報價單編號", sortable: true },
      { key: "created_at", label: "創建日期", sortable: true },
    ];
    const sortBy = ref("id");
    const isSortDirDesc = ref(true);

    const refReceiptListTable = ref(null);
    const receiptTablemColumns = ref([
      { key: "id", stickyColumn: true, label: "收據編號", sortable: true },
      { key: "address", label: "施工地址", sortable: true },
      { key: "export", label: "列印收據", sortable: true },
      { key: "show_details", label: "支付項目", sortable: true },
      { key: "amount", label: "金額", formatter: formatCurrency },
      { key: "payment_method.name", label: "收款方式", sortable: true },
      { key: "receipt_date", label: "收據日期", sortable: true },
    ]);
    const sortReceiptBy = ref("id");
    const isSortDirDescReceipt = ref(true);

    const getQuotation = (id) => {
      if (!quotationOptions.value?.length || !id)
        return { quotation_number: "- -" };
      const obj = quotationOptions.value.find((ele) => ele.id == id) || {
        quotation_number: "- -",
      };
      return { ...obj };
    };

    const getPaymentMethod = (id) => {
      if (!paymentMethodOptions.value?.length || !id) return { name: "- -" };
      return (
        paymentMethodOptions.value.find((ele) => ele.id == id) || {
          name: "- -",
        }
      );
    };

    const getInvoiceType = (id) => {
      if (!invoiceTypeOptions.value?.length || !id) return { name: "- -" };
      return (
        invoiceTypeOptions.value.find((ele) => ele.id == id) || { name: "- -" }
      );
    };

    const getProduct = (id) => {
      if (!productOptions.value?.length || !id)
        return { product_name: "- -", unit: "次" };
      const obj = productOptions.value.find((ele) => ele.id == id) || {
        product_name: "- -",
      };
      return { ...{ unit: "次" }, ...obj };
    };

    const getProductCategory = (id) => {
      if (id) {
        const obj =
          productCategoryOptions.value &&
          productCategoryOptions.value.length > 0 &&
          productCategoryOptions.value.find((ele) => ele.id == id);
        return { ...obj };
      } else {
        return "- -";
      }
    };
    const getProductUnit = (productId) => {
      if (!productId || !productOptions.value || !productOptions.value.length) {
        return '件';
      }
      const product = productOptions.value.find(function(ele) {
        return ele.id === productId;
      });
      return product ? (product.unit || '件') : '件';
    };

    const refetchData = () => {
      fetchCustomer();
      refQuotationListTable.value.refresh();
      refInvoiceListTable.value.refresh();
      refReceiptListTable.value.refresh();
    };

    const fetchQuotations = (ctx, callback) => {
      if (router.currentRoute.params.quotating && init) {
        filter_quotating.value = router.currentRoute.params.quotating;
        init = false;
      }
      store
        .dispatch("customer/fetchQuotations", {
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          customer_id: router.currentRoute.params.id,
        })
        .then((response) => {
          const { quotations } = response.data;
          callback(quotations);
        })
        .catch((err) => {
          console.log(err);
          toast({
            component: ToastificationContent,
            props: {
              title: "獲取貸款清單時出錯",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const fetchInvoices = (ctx, callback) => {
      store
        .dispatch("customer/fetchInvoices", {
          sortBy: sortBy.value,
          sortDesc: isSortDirDesc.value,
          customer_id: router.currentRoute.params.id,
        })
        .then((response) => {
          const { invoices } = response.data;
          callback(invoices);
        })
        .catch((err) => {
          console.log(err);
          toast({
            component: ToastificationContent,
            props: {
              title: "獲取發票清單時出錯",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const fetchReceipts = (ctx, callback) => {
      store
        .dispatch("customer/fetchReceipts", {
          sortBy: sortReceiptBy.value,
          sortDesc: isSortDirDescReceipt.value,
          customer_id: router.currentRoute.params.id,
        })
        .then((response) => {
          const { receipts } = response.data;
          callback(receipts);
        })
        .catch((err) => {
          console.log(err);
          toast({
            component: ToastificationContent,
            props: {
              title: "獲取收據清單時出錯",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const fetchCustomer = () => {
      store
        .dispatch("customer/fetchCustomer", {
          id: router.currentRoute.params.id,
        })
        .then((response) => {
          customerData.value = response.data.entity;
        })
        .catch((error) => {
          console.log("error when fetching customer", error);
          if (error.response.status === 404) {
            customerData.value = undefined;
          }
        });
    };
    const invoiceTypeOptions = ref([]);
    const quotationOptions = ref([]);
    const paymentMethodOptions = ref([]);
    const productOptions = ref([]);
    const productCategoryOptions = ref([]);
    const workerOptions = ref([]);
    const refetchOption = () => {
      store
        .dispatch("app/fetchOptionList", {
          quotation_types: true,
          payment_methods: true,
          quotations: true,
          products: true,
          product_categories: true,
          workers: true,
        })
        .then((response) => {
          invoiceTypeOptions.value = response.data.quotation_types;
          paymentMethodOptions.value = response.data.payment_methods;
          quotationOptions.value = response.data.quotations;
          productOptions.value = response.data.products;
          productCategoryOptions.value = response.data.product_categories;
          workerOptions.value = response.data.workers;
        });
    };



    // 新增一個工具函數來安全地查找worker（與 orderView 一致）
    const findWorker = (workerId, workers) => {
      if (!workers || !workers.length) return { name_cn: "--", position: "--" };
      return (
        workers.find((w) => w.id === workerId) || {
          name_cn: "--",
          position: "--",
        }
      );
    };

    // 獲取師傅姓名（使用 findWorker 方法）
    const getWorkerName = (workerId) => {
      // 使用 findWorker 方法來獲取師傅信息
      const worker = findWorker(workerId, workerOptions.value);
      return worker.name_cn || `師傅 ${workerId}`;
    };

    onMounted(() => {
      fetchCustomer();
      refetchOption();
    });

    return {
      customerData,
      refetchData,
      refQuotationListTable,
      refInvoiceListTable,
      refReceiptListTable,
      getPaymentMethod,
      getInvoiceType,
      getQuotation,
      getProduct,
      getProductCategory,
      getProductUnit,
      invoiceTypeOptions,
      paymentMethodOptions,
      quotationOptions,
      productOptions,
      workerOptions,
      fetchInvoices,
      invoiceColumns,
      isSortDirDesc,
      fetchReceipts,
      receiptTablemColumns,
      isSortDirDescReceipt,
      sortBy,
      sortReceiptBy,
      fetchQuotations,
      quotationColumns,
      sortByQuotation,
      isSortDirDescQuotation,
      findWorker,
      getWorkerName,
    };
  },
};
</script>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@/assets/scss/variables/_variables.scss";
.pdf-table-wrapper-box table tbody tr td {
  &:nth-child(3) {
    width: 110px !important;
    max-width: 110px !important;
    min-width: 110px !important;
  }
}
</style>
<style lang="scss" scoped>
.info-wrap {
  .info-label {
    display: inline-block;
    width: 100px;
  }
  .info-item {
    margin-bottom: 1rem;
    .icon {
      display: inline-block;
      margin-right: 1rem;
      width: 15px;
      svg {
        margin: auto;
        display: block;
      }
    }
  }
}
</style>
